import { useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Stepper } from '@/components/ui/stepper';
import { usePricingWebSocket } from '@/hooks/usePricingWebSocket';
import { UploadStep } from './steps/UploadStep';
import { PassengerStep } from './steps/PassengerStep';
import { FlightSegmentsStep } from './steps/FlightSegmentsStep';
import { ResultsStep } from './steps/ResultsStep';
import type { FlightSegment, Passenger, ExtractionResult } from '@/types/pricing';

const STEPS = [
  { id: 'upload', label: 'Upload' },
  { id: 'passenger', label: 'Passenger' },
  { id: 'segments', label: 'Segments' },
  { id: 'results', label: 'Results' },
];

function generateSegmentId(): string {
  return Math.random().toString(36).substring(2, 9);
}

// Map booking class categories to single-letter codes for FQP commands
const categoryToBookingClass: Record<string, string> = {
  'Economy': 'Y',
  'Premium Economy': 'W',
  'Business': 'J',
  'First': 'F',
  'Suites': 'F',
};

function createEmptySegment(): FlightSegment {
  return {
    id: generateSegmentId(),
    origin: '',
    destination: '',
    airline: '',
    bookingClass: '',
    flightDate: '',
    departureTime: '',
    arrivalTime: '',
  };
}

export function PricingWizard() {
  const { status, sendExtractionRequest, sendFQPCommand, sendFQQCommand } = usePricingWebSocket();

  // Step tracking
  const [currentStep, setCurrentStep] = useState(0);
  const [highestStep, setHighestStep] = useState(0);

  // Form data
  const [passenger, setPassenger] = useState<Passenger>({ firstName: '', lastName: '' });
  const [ticketingDate, setTicketingDate] = useState('');
  const [segments, setSegments] = useState<FlightSegment[]>([createEmptySegment()]);

  // Navigation handlers
  const goToStep = useCallback((step: number) => {
    setCurrentStep(step);
    if (step > highestStep) {
      setHighestStep(step);
    }
  }, [highestStep]);

  const handleStepClick = useCallback((step: number) => {
    if (step <= highestStep) {
      setCurrentStep(step);
    }
  }, [highestStep]);

  // Upload step handlers
  const handleExtract = useCallback(async (file: File) => {
    return sendExtractionRequest(file);
  }, [sendExtractionRequest]);

  const handleExtractionComplete = useCallback((result: ExtractionResult) => {
    // Populate passenger data
    if (result.passengers && result.passengers.length > 0) {
      const firstPassenger = result.passengers[0];
      setPassenger({
        firstName: firstPassenger.first_name || '',
        lastName: firstPassenger.last_name || '',
      });
    }

    // Populate ticketing date
    if (result.booking_date) {
      setTicketingDate(result.booking_date);
    }

    // Populate segments
    // Backend returns: dep_iata, arr_iata, carrier, dep_time, arr_time, dep_date, booking_class_category
    if (result.flight_segments && result.flight_segments.length > 0) {
      const mappedSegments = result.flight_segments.map((seg: any) => {
        // Convert category name (e.g., "Economy") to single-letter code (e.g., "Y")
        const rawClass = seg.booking_class_category || seg.booking_class || '';
        const bookingClass = categoryToBookingClass[rawClass] || rawClass || 'Y';

        return {
          id: generateSegmentId(),
          origin: seg.dep_iata || '',
          destination: seg.arr_iata || '',
          airline: seg.carrier || '',
          bookingClass,
          flightDate: seg.dep_date || '',
          departureTime: seg.dep_time || '',
          arrivalTime: seg.arr_time || '',
        };
      });
      setSegments(mappedSegments);
    }

    // Move to passenger step
    goToStep(1);
  }, [goToStep]);

  const handleSkipUpload = useCallback(() => {
    goToStep(1);
  }, [goToStep]);

  // Reset handler
  const handleReset = useCallback(() => {
    setCurrentStep(0);
    setHighestStep(0);
    setPassenger({ firstName: '', lastName: '' });
    setTicketingDate('');
    setSegments([createEmptySegment()]);
  }, []);

  // FQP/FQQ handlers (now use REST API with warm session - no progress needed)
  const handleExecuteFQP = useCallback(async (command: string) => {
    return sendFQPCommand(command);
  }, [sendFQPCommand]);

  const handleExecuteFQQ = useCallback(async (fareNumber: string) => {
    return sendFQQCommand(fareNumber);
  }, [sendFQQCommand]);

  return (
    <div className="max-w-4xl mx-auto">
      <Card>
        {/* Stepper integrated into card header */}
        <div className="px-6 pt-5 pb-4 border-b">
          <Stepper
            steps={STEPS}
            currentStep={currentStep}
            highestStep={highestStep}
            onStepClick={handleStepClick}
          />
        </div>

        {/* Step Content */}
        <CardContent className="py-5">
          {currentStep === 0 && (
            <UploadStep
              wsStatus={status}
              onExtract={handleExtract}
              onExtractionComplete={handleExtractionComplete}
              onSkip={handleSkipUpload}
            />
          )}

          {currentStep === 1 && (
            <PassengerStep
              passenger={passenger}
              ticketingDate={ticketingDate}
              onPassengerChange={setPassenger}
              onTicketingDateChange={setTicketingDate}
              onBack={() => goToStep(0)}
              onNext={() => goToStep(2)}
            />
          )}

          {currentStep === 2 && (
            <FlightSegmentsStep
              segments={segments}
              onSegmentsChange={setSegments}
              onBack={() => goToStep(1)}
              onNext={() => goToStep(3)}
            />
          )}

          {currentStep === 3 && (
            <ResultsStep
              passenger={passenger}
              ticketingDate={ticketingDate}
              segments={segments}
              onBack={() => goToStep(2)}
              onReset={handleReset}
              onExecuteFQP={handleExecuteFQP}
              onExecuteFQQ={handleExecuteFQQ}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
